"use client";

// The COMMAND v2 left panel — new chat, search, Projects and chat history.
// Command v2 pages (Agents, agent settings), where interactions prime the
// store and navigate back to the chat page, so the sidebar never swaps out.

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Folder,
  FolderOpen,
  FolderPlus,
  MessagesSquare,
  MoreHorizontal,
  Pencil,
  Pin,
  Plus,
  Search,
  Settings,
  SquarePen,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { COMMAND_V2_DIALOG_CONTENT_CLASS } from "@/components/command-team-v2/utils/dialog-styles";
import { useCommandV2Schedules } from "@/hooks/use-command-v2-schedules";

import { compareSessions, useCommandPrototype } from "../prototype/store";
import type { ProtoChatSession, ProtoProject } from "../prototype/types";
import { ChatRowMenu, RenameChatDialog } from "./chat-actions";
import { ChatStatusDot } from "./chat-status-dot";
import {
  OTHER_CHATS_DROP_KEY,
  chatDropHandlers,
  setChatDragData,
} from "./chat-dnd";
import {
  PROJECTS_PAGE_PATH,
  SCHEDULED_PAGE_PATH,
  useSidebarLists,
} from "./sidebar-lists";
import { DeleteProjectsDialog, describeProjectDeletion } from "./delete-projects-dialog";
import { SearchEverythingDialog } from "./search-dialog";
import {
  CommandProjectFolderSkeleton,
  CommandSidebarListSkeleton,
} from "./command-v2-loading-skeletons";
import { CommandSidebarScrollArea } from "@/components/command-team-v2/layout/command-sidebar-scroll-area";

// the collapsed/expanded choice survives refreshes and page hops
const COLLAPSED_STORAGE_KEY = "aios:commandv2-panel-collapsed";
/** How long a pending scroll to the open chat's row may wait (row loading, panel collapsed). */
const REVEAL_TIMEOUT_MS = 10_000;

// user-dragged panel width, persisted across refreshes
const PANEL_WIDTH_KEY = "aios:commandv2-panel-width";
const PANEL_DEFAULT_WIDTH = 280;
const PANEL_MIN_WIDTH = 240;
const PANEL_MAX_WIDTH = 480;
const clampPanelWidth = (width: number) =>
  Math.min(Math.max(width, PANEL_MIN_WIDTH), PANEL_MAX_WIDTH);

function ChatRow({
  session,
  active,
  projects,
  onSelect,
  onMove,
  onDelete,
  onTogglePin,
  onRename,
  onDragEnd,
}: {
  session: ProtoChatSession;
  active: boolean;
  projects: ProtoProject[];
  onSelect: () => void;
  onMove: (projectId: string | null) => void;
  onDelete: () => void;
  onTogglePin: () => void;
  onRename: () => void;
  onDragEnd?: () => void;
}) {
  return (
    <div
      draggable
      data-session-id={session.id}
      onDragStart={(event) => setChatDragData(event, session.id)}
      onDragEnd={onDragEnd}
      className={cn(
        "group flex items-center gap-0.5 rounded-lg pl-2 pr-1",
        active ? "bg-violet-50 dark:bg-violet-950/30" : "hover:bg-muted/70"
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? "page" : undefined}
        className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left"
      >
        <ChatStatusDot session={session} />
        {session.fromSchedule ? (
          <Clock className="size-3 shrink-0 text-primary/70" />
        ) : null}
        <span
          className={cn(
            "truncate text-[13px]",
            active ? "font-medium text-foreground" : "text-foreground/80"
          )}
        >
          {session.title}
        </span>
      </button>
      {/* hover quick-pin: outlined while unpinned, filled + always visible when pinned */}
      <button
        type="button"
        onClick={onTogglePin}
        title={session.pinned ? "Unpin" : "Pin to top"}
        aria-label={
          session.pinned
            ? `Unpin “${session.title}”`
            : `Pin “${session.title}” to top`
        }
        className={cn(
          // hidden (not opacity-0) while idle so the title gets the full width
          "size-6 shrink-0 items-center justify-center rounded",
          session.pinned
            ? "flex text-primary/70 hover:text-primary"
            : "hidden text-muted-foreground hover:text-foreground group-hover:flex"
        )}
      >
        <Pin className={cn("size-3 rotate-45", session.pinned && "fill-current")} />
      </button>
      <ChatRowMenu
        session={session}
        projects={projects}
        onTogglePin={onTogglePin}
        onRename={onRename}
        onMove={onMove}
        onDelete={onDelete}
      />
    </div>
  );
}

export function NewProjectButton({ onCreate }: { onCreate: (name: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate(trimmed);
    setName("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="size-6 p-0 text-muted-foreground hover:text-foreground"
          title="New project"
        >
          <FolderPlus className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3">
        <p className="text-sm font-semibold">New project</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Group related chats into one folder.
        </p>
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
          }}
          placeholder="Project name"
          className="mt-2 h-8 text-sm"
          autoFocus
        />
        <div className="mt-2 flex justify-end gap-2">
          <Button
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className="h-7 rounded-full px-3 text-xs"
            disabled={!name.trim()}
            onClick={submit}
          >
            Create
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Exported so the projects page renames with the exact same dialog. */
export function RenameProjectDialog({
  project,
  onClose,
  onRename,
}: {
  project: ProtoProject | null;
  onClose: () => void;
  onRename: (projectId: string, name: string) => void;
}) {
  const [name, setName] = useState("");

  useEffect(() => {
    if (project) setName(project.name);
  }, [project]);

  const submit = () => {
    const trimmed = name.trim();
    if (!project || !trimmed) return;
    onRename(project.id, trimmed);
    onClose();
  };

  return (
    <Dialog open={!!project} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn(COMMAND_V2_DIALOG_CONTENT_CLASS, "max-w-sm")}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex shrink-0 items-center justify-center rounded-[9px] bg-violet-100 p-2 dark:bg-violet-900/30">
              <Pencil className="size-4 text-primary" />
            </div>
            <div className="min-w-0 space-y-1">
              <DialogTitle className="text-left text-lg font-semibold tracking-tight">
                Rename project
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Chats inside keep their titles — only the folder changes.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <div className="mt-6">
          <Label className="font-semibold text-foreground">Project name</Label>
          <Input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") submit();
            }}
            className="mt-2"
          />
        </div>

        <div className="mt-6 flex shrink-0 items-center justify-end gap-4 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-foreground transition-opacity hover:opacity-70"
          >
            Cancel
          </button>
          <Button
            type="button"
            onClick={submit}
            disabled={!name.trim()}
            className="h-10 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Project settings — name, description, instructions and visibility in one
// dialog, opened from the project row's ⋯ menu.

export function ProjectSettingsDialog({
  project,
  onClose,
  onSave,
}: {
  project: ProtoProject | null;
  onClose: () => void;
  onSave: (
    projectId: string,
    patch: {
      name: string;
      description: string;
      instructions: string;
      visibility: "personal" | "team";
    }
  ) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [visibility, setVisibility] = useState<"personal" | "team">("personal");

  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description ?? "");
      setInstructions(project.instructions ?? "");
      setVisibility(project.visibility ?? "personal");
    }
  }, [project]);

  const submit = () => {
    const trimmed = name.trim();
    if (!project || !trimmed) return;
    onSave(project.id, {
      name: trimmed,
      description: description.trim(),
      instructions: instructions.trim(),
      visibility,
    });
    onClose();
  };

  return (
    <Dialog open={!!project} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn(COMMAND_V2_DIALOG_CONTENT_CLASS, "max-w-md")}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex shrink-0 items-center justify-center rounded-[9px] bg-violet-100 p-2 dark:bg-violet-900/30">
              <Settings className="size-4 text-primary" />
            </div>
            <div className="min-w-0 space-y-1">
              <DialogTitle className="text-left text-lg font-semibold tracking-tight">
                Project settings
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Update how this project appears and how Command uses it.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label className="font-semibold text-foreground">Name</Label>
            <Input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-foreground">Description</Label>
            <Textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Optional short description"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-foreground">Instructions</Label>
            <Textarea
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
              placeholder="Optional context for chats in this project"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-foreground">Visibility</Label>
            <Select
              value={visibility}
              onValueChange={(value) => setVisibility(value as "personal" | "team")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">Personal</SelectItem>
                <SelectItem value="team">Team</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-6 flex shrink-0 items-center justify-end gap-4 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-foreground transition-opacity hover:opacity-70"
          >
            Cancel
          </button>
          <Button
            type="button"
            onClick={submit}
            disabled={!name.trim()}
            className="h-10 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

export function CommandV2Panel({
  onOpenChat,
  onOpenProject,
  onNewChat,
  onChatDeleted,
  onProjectDeleted,
  className,
  forceExpanded = false,
}: {
  onOpenChat: (sessionId: string) => void;
  onOpenProject: (projectId: string) => void;
  onNewChat: (projectId?: string | null) => void;
  /** Fired before the store delete, with the pre-delete state still current. */
  onChatDeleted?: (sessionId: string) => void;
  onProjectDeleted?: (projectId: string) => void;
  className?: string;
  /** Expanded, full-width panel for the mobile sidebar sheet. */
  forceExpanded?: boolean;
}) {
  const {
    state,
    actions,
    loading,
    sessionsLoadMoreRef,
    hasMoreSessions,
    isFetchingMoreSessions,
  } = useCommandPrototype();
  const pathname = usePathname();
  const [hideSidebarEarly, setHideSidebarEarly] = useState(false);

  useEffect(() => {
    const onPresetsPage = pathname?.includes("/command-team/prompts") ?? false;
    const onPresets = (event: Event) => {
      setHideSidebarEarly(onPresetsPage || (event as CustomEvent<boolean>).detail === true);
    };
    window.addEventListener("command-presets-sidebar", onPresets);
    setHideSidebarEarly(onPresetsPage);
    return () => window.removeEventListener("command-presets-sidebar", onPresets);
  }, [pathname]);
  const { activeCount: scheduledCount } = useCommandV2Schedules({ countOnly: true });
  // kept for the list-filter plumbing below; the old inline header search
  // set it — searching now happens in the search-everything popup instead
  const [search] = useState("");
  // the nav row's search-everything popup (chats + projects)
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<ProtoProject | null>(null);
  const [settingsTarget, setSettingsTarget] = useState<ProtoProject | null>(null);
  const [renameChatTarget, setRenameChatTarget] = useState<ProtoChatSession | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // ── Drag-resize on the right edge (expanded panel only) ──
  const asideRef = useRef<HTMLElement | null>(null);
  const [panelWidth, setPanelWidth] = useState(PANEL_DEFAULT_WIDTH);
  const [resizing, setResizing] = useState(false);
  // the edge tab doubles as a drag handle — tracks whether a press moved
  // (drag = resize) or stayed put (click = toggle)
  const tabDragRef = useRef<{ startX: number; moved: boolean } | null>(null);

  useEffect(() => {
    try {
      const saved = Number(localStorage.getItem(PANEL_WIDTH_KEY));
      if (saved >= PANEL_MIN_WIDTH) setPanelWidth(clampPanelWidth(saved));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth));
    } catch {
      /* ignore */
    }
  }, [panelWidth]);

  useEffect(() => {
    if (!resizing) return;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [resizing]);
  // which drop target (project id / Other chats) the dragged chat is over
  const [dragOver, setDragOver] = useState<string | null>(null);
  // folders the user has closed (all start open); opening the active chat's folder overrides it
  const [closedProjectIds, setClosedProjectIds] = useState<ReadonlySet<string>>(() => new Set());
  const setProjectOpen = (projectId: string, open: boolean) =>
    setClosedProjectIds((current) => {
      if (current.has(projectId) !== open) return current;
      const next = new Set(current);
      if (open) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  // pending project deletion, awaiting the confirm dialog
  const [deletingProjects, setDeletingProjects] = useState<ProtoProject[] | null>(null);
  // list caps + agent/project drag-to-reorder, shared with the Talk to Command drawer
  const lists = useSidebarLists({
    setDragOver,
    onDropChatIntoProject: (sessionId, projectId) => moveChat(sessionId, projectId),
  });

  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(COLLAPSED_STORAGE_KEY) === "1");
    } catch {
      /* ignore */
    }
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, current ? "0" : "1");
      } catch {
        /* ignore */
      }
      return !current;
    });
  };

  const query = search.trim().toLowerCase();
  const matchesSearch = (session: ProtoChatSession) =>
    !query || session.title.toLowerCase().includes(query);
  const chatsForProject = (projectId: string) =>
    state.sessions
      .filter((session) => session.projectId === projectId && matchesSearch(session))
      .sort(compareSessions);
  const otherChats = state.sessions
    .filter((session) => !session.projectId && matchesSearch(session))
    .sort(compareSessions);
  const nothingMatches =
    query.length > 0 &&
    otherChats.length === 0 &&
    state.projects.every((project) => chatsForProject(project.id).length === 0);

  const confirmDeleteProjects = () => {
    const doomed = deletingProjects ?? [];
    if (doomed.length === 0) return;
    const { summary, chatCount } = describeProjectDeletion(doomed, state.sessions);
    // Tell the host page about every chat that goes, not just the folder —
    // one of them may be the chat it's currently showing.
    const ids = new Set(doomed.map((project) => project.id));
    state.sessions
      .filter((session) => session.projectId && ids.has(session.projectId))
      .forEach((session) => onChatDeleted?.(session.id));
    doomed.forEach((project) => onProjectDeleted?.(project.id));
    actions.deleteProjects(doomed.map((project) => project.id));
    toast.success(
      doomed.length === 1 ? "Project deleted" : `${doomed.length} projects deleted`,
      {
        description: chatCount
          ? `${summary} were removed.`
          : `${summary} was removed.`,
      }
    );
  };

  const moveChat = (sessionId: string, projectId: string | null) => {
    const session = state.sessions.find((item) => item.id === sessionId);
    if (!session || (session.projectId ?? null) === projectId) return;
    actions.moveChatToProject(sessionId, projectId);
    toast.success("Chat moved", {
      description: projectId
        ? `Moved to “${state.projects.find((item) => item.id === projectId)?.name}”.`
        : "Moved to Other chats.",
    });
  };

  const isCollapsed = forceExpanded ? false : collapsed;

  // Bring the open chat's row into view once, e.g. after it was opened from a link: open its project
  // folder (and include it in the shown projects), then scroll the list if the row is out of sight.
  // A row clicked here is already in view. A reveal still waiting after a while (the row never loaded,
  // the panel stayed collapsed) is dropped rather than moving the list under the user later.
  const revealRef = useRef({ sessionId: null as string | null, since: 0, done: true });
  const openChatFromList = (sessionId: string) => {
    revealRef.current = { sessionId, since: Date.now(), done: true };
    onOpenChat(sessionId);
  };
  const activeSessionId = state.activeSessionId ?? null;
  const activeChat = activeSessionId
    ? state.sessions.find((session) => session.id === activeSessionId) ?? null
    : null;
  const activeChatId = activeChat?.id ?? null;
  const activeChatProjectId = activeChat?.projectId ?? null;
  const revealProject = lists.projects.reveal;
  const shownProjectCount = lists.projects.visible.length;
  useEffect(() => {
    if (!activeSessionId) return;
    if (revealRef.current.sessionId !== activeSessionId) {
      revealRef.current = { sessionId: activeSessionId, since: Date.now(), done: false };
    }
    const reveal = revealRef.current;
    if (reveal.done) return;
    if (Date.now() - reveal.since > REVEAL_TIMEOUT_MS) {
      reveal.done = true;
      return;
    }
    if (!activeChatId || isCollapsed) return;
    if (activeChatProjectId) {
      revealProject(activeChatProjectId);
      setClosedProjectIds((current) => {
        if (!current.has(activeChatProjectId)) return current;
        const next = new Set(current);
        next.delete(activeChatProjectId);
        return next;
      });
    }
    const frame = requestAnimationFrame(() => {
      const row = asideRef.current?.querySelector<HTMLElement>(
        `[data-session-id="${CSS.escape(activeChatId)}"]`
      );
      // Folder still opening: the next change to the list tries again.
      if (!row) return;
      reveal.done = true;
      // Panel hidden at this width: nothing to show.
      if (row.offsetParent === null) return;
      const viewport = row.closest<HTMLElement>("[data-radix-scroll-area-viewport]");
      if (!viewport) return;
      const rowBox = row.getBoundingClientRect();
      const viewBox = viewport.getBoundingClientRect();
      if (rowBox.top >= viewBox.top && rowBox.bottom <= viewBox.bottom) return;
      const outOfView = rowBox.bottom <= viewBox.top || rowBox.top >= viewBox.bottom;
      // Centre a row that's out of sight; nudge a partly hidden one just into view.
      viewport.scrollTop += outOfView
        ? rowBox.top - viewBox.top - (viewBox.height - rowBox.height) / 2
        : rowBox.top < viewBox.top
          ? rowBox.top - viewBox.top - 8
          : rowBox.bottom - viewBox.bottom + 8;
    });
    return () => cancelAnimationFrame(frame);
  }, [
    activeSessionId,
    activeChatId,
    activeChatProjectId,
    isCollapsed,
    revealProject,
    closedProjectIds,
    shownProjectCount,
  ]);

  return (
    <aside
      ref={asideRef}
      style={isCollapsed || forceExpanded ? undefined : { width: panelWidth }}
      className={cn(
        // no overflow-hidden: the collapse tab hangs half over the right
        // edge; the inner columns clip their own content
        "relative shrink-0 border-r border-border bg-background transition-[width] duration-200 ease-linear",
        forceExpanded ? "flex h-full min-h-0 w-full" : hideSidebarEarly ? "hidden xl:flex" : "hidden lg:flex",
        !forceExpanded && (isCollapsed ? "w-12" : "w-[280px]"),
        // width follows the pointer directly while dragging
        resizing && "transition-none",
        className
      )}
    >
      {!isCollapsed && !forceExpanded ? (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize Command sidebar"
          onPointerDown={(event) => {
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            setResizing(true);
          }}
          onPointerMove={(event) => {
            if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
            const left = asideRef.current?.getBoundingClientRect().left ?? 0;
            setPanelWidth(clampPanelWidth(event.clientX - left));
          }}
          onPointerUp={(event) => {
            event.currentTarget.releasePointerCapture(event.pointerId);
            setResizing(false);
          }}
          onPointerCancel={() => setResizing(false)}
          onLostPointerCapture={() => setResizing(false)}
          onDoubleClick={() => setPanelWidth(PANEL_DEFAULT_WIDTH)}
          className={cn(
            // 10px grab strip on the edge — a hairline is too easy to miss;
            // double-click snaps back to the default width. The class hook
            // lets the mobile sheet hide it (width is forced there).
            "command-panel-resize absolute inset-y-0 right-0 z-10 w-2.5 cursor-col-resize touch-none transition-colors",
            resizing ? "bg-primary/30" : "hover:bg-primary/20"
          )}
        />
      ) : null}
      {/* Collapse/expand tab riding the panel edge — same style as the main
          sidebar's rail arrow. Hidden in the mobile sheet via the class hook.
          A plain click toggles; dragging it resizes the panel (dragging out
          of the collapsed rail expands first, then follows the pointer). */}
      <button
        type="button"
        onPointerDown={(event) => {
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          tabDragRef.current = { startX: event.clientX, moved: false };
          if (!isCollapsed) setResizing(true);
        }}
        onPointerMove={(event) => {
          if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
          const drag = tabDragRef.current;
          if (!drag) return;
          // a few px of jitter still counts as a click
          if (Math.abs(event.clientX - drag.startX) > 4) drag.moved = true;
          if (!drag.moved) return;
          if (isCollapsed) {
            // pulling right out of the rail expands, then the same drag
            // keeps resizing from the pointer position
            if (event.clientX - drag.startX > 24) {
              toggleCollapsed();
              setResizing(true);
            }
            return;
          }
          const left = asideRef.current?.getBoundingClientRect().left ?? 0;
          setPanelWidth(clampPanelWidth(event.clientX - left));
        }}
        onPointerUp={(event) => {
          event.currentTarget.releasePointerCapture(event.pointerId);
          setResizing(false);
          // no real movement → it was a click: toggle
          if (!tabDragRef.current?.moved) toggleCollapsed();
          tabDragRef.current = null;
        }}
        onPointerCancel={() => {
          setResizing(false);
          tabDragRef.current = null;
        }}
        onClick={(event) => {
          // keyboard activation (Enter/Space) — pointer clicks toggle in
          // onPointerUp, so a drag never double-toggles
          if (event.detail === 0) toggleCollapsed();
        }}
        title={isCollapsed ? "Expand Command sidebar" : "Minimize Command sidebar"}
        aria-label={
          isCollapsed ? "Expand Command sidebar" : "Minimize Command sidebar"
        }
        className={cn(
          "command-panel-minimize absolute -right-[9px] top-1/2 z-20 flex h-9 w-[18px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-md border border-border bg-background text-muted-foreground shadow-sm transition-colors touch-none hover:border-primary/40 hover:text-primary",
          forceExpanded && "hidden"
        )}
      >
        <ChevronRight
          className={cn("size-3 transition-transform", !isCollapsed && "rotate-180")}
        />
      </button>
      {isCollapsed ? (
        <div className="flex h-full w-12 shrink-0 flex-col items-center gap-2 py-3">
          <button
            type="button"
            onClick={() => onNewChat(null)}
            title="New chat"
            aria-label="New chat"
            className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <SquarePen className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => setSearchDialogOpen(true)}
            title="Search chats and projects"
            aria-label="Search chats and projects"
            className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Search className="size-4" />
          </button>
          <Link
            href={SCHEDULED_PAGE_PATH}
            title="Scheduled"
            aria-label="Scheduled"
            className={cn(
              "flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
              pathname === SCHEDULED_PAGE_PATH && "bg-violet-50 text-primary dark:bg-violet-950/30"
            )}
          >
            <Clock className="size-4" />
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                title="Projects"
                aria-label="Projects"
                className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Folder className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start" className="w-56">
              {state.projects.map((project) => (
                <DropdownMenuItem
                  key={project.id}
                  onClick={() => onOpenProject(project.id)}
                >
                  <Folder className="mr-2 size-3.5 text-primary" />
                  <span className="min-w-0 flex-1 truncate">{project.name}</span>
                </DropdownMenuItem>
              ))}
              {state.projects.length === 0 ? (
                <DropdownMenuItem disabled>No projects yet</DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                title="Other chats"
                aria-label="Other chats"
                className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <MessagesSquare className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="right"
              align="start"
              className="max-h-72 w-56 overflow-y-auto"
            >
              {state.sessions
                .filter((session) => !session.projectId)
                .sort(compareSessions)
                .map((session) => (
                  <DropdownMenuItem
                    key={session.id}
                    onClick={() => onOpenChat(session.id)}
                    title={session.title}
                  >
                    <ChatStatusDot session={session} />
                    {session.fromSchedule ? (
                      <Clock className="size-3 shrink-0 text-primary/70" />
                    ) : null}
                    <span className="min-w-0 flex-1 truncate">
                      {session.title}
                    </span>
                  </DropdownMenuItem>
                ))}
              {state.sessions.every((session) => session.projectId) ? (
                <DropdownMenuItem disabled>No chats yet</DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
      {!isCollapsed ? (
        <div className="flex h-full w-full min-w-0 flex-col">
          {/* Top nav — plain rows at the very top (the edge tab replaced the
              old header's minimize + search icons): New chat, Search,
              Scheduled */}
          <div className="shrink-0 space-y-0.5 border-b border-border px-2 pb-2 pt-3">
            <button
              type="button"
              onClick={() => onNewChat(null)}
              className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-[13px] font-medium transition-colors hover:bg-muted"
            >
              <SquarePen className="size-4 shrink-0 text-muted-foreground" />
              New chat
            </button>
            <button
              type="button"
              onClick={() => setSearchDialogOpen(true)}
              className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-[13px] font-medium transition-colors hover:bg-muted"
            >
              <Search className="size-4 shrink-0 text-muted-foreground" />
              Search
            </button>
            <Link
              href={SCHEDULED_PAGE_PATH}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-[13px] font-medium transition-colors hover:bg-muted",
                pathname === SCHEDULED_PAGE_PATH &&
                  "bg-violet-50 text-primary dark:bg-violet-950/30"
              )}
            >
              <Clock className="size-4 shrink-0 text-muted-foreground" />
              Scheduled
              {scheduledCount > 0 ? (
                <span className="ml-auto rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-primary dark:bg-violet-900/30">
                  {scheduledCount}
                </span>
              ) : null}
            </Link>
          </div>

          <CommandSidebarScrollArea className="px-2 py-2">
            <div className="flex items-center justify-between px-2 pb-1">
              <div className="flex min-w-0 items-center gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Projects
                </p>
                <Link
                  href={PROJECTS_PAGE_PATH}
                  className={cn(
                    "text-[11px] font-medium transition-colors hover:text-foreground",
                    pathname === PROJECTS_PAGE_PATH
                      ? "text-primary"
                      : "text-muted-foreground"
                  )}
                >
                  View all
                </Link>
              </div>
              <NewProjectButton
                onCreate={(name) => {
                  actions.createProject(name);
                  toast.success("Project created", {
                    description: `“${name}” added — move chats into it from their menu.`,
                  });
                }}
              />
            </div>

            {/* Wrapper catches folder drops that miss a row — over another
                folder's chats, or in the space between them. */}
            <div {...lists.projects.listProps}>
            {mounted && loading?.projects && state.projects.length === 0 ? (
              <div className="space-y-1 py-1">
                {Array.from({ length: 3 }).map((_, index) => (
                  <CommandProjectFolderSkeleton key={index} />
                ))}
              </div>
            ) : null}
            {lists.projects.visible.map((project) => {
              const chats = chatsForProject(project.id);
              if (query && chats.length === 0) return null;
              return (
                <Collapsible
                  key={project.id}
                  open={!closedProjectIds.has(project.id)}
                  onOpenChange={(open) => setProjectOpen(project.id, open)}
                  className="group/proj"
                >
                  {/* chevron toggles the folder; the name opens the project view;
                      hover actions (new chat, ⋯ menu) replace the old count badge;
                      the row is a drop target for dragged chats AND draggable
                      itself, so folders can be reordered in place */}
                  <div
                    {...lists.projects.rowProps(project.id)}
                    className={cn(
                      "group/projrow flex w-full cursor-grab items-center gap-0.5 rounded-lg pl-1 pr-1 transition-opacity hover:bg-muted/70 active:cursor-grabbing",
                      dragOver === project.id &&
                        "bg-violet-50 ring-1 ring-inset ring-primary/40 dark:bg-violet-950/30",
                      // The row being dragged fades where it now sits — the
                      // list has already re-laid-out around it.
                      lists.projects.draggingId === project.id && "opacity-50"
                    )}
                  >
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        aria-label={`Toggle “${project.name}”`}
                        className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <ChevronRight className="size-3.5 transition-transform group-data-[state=open]/proj:rotate-90" />
                      </button>
                    </CollapsibleTrigger>
                    <button
                      type="button"
                      onClick={() => onOpenProject(project.id)}
                      className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left"
                    >
                      <Folder className="size-3.5 shrink-0 text-primary group-data-[state=open]/proj:hidden" />
                      <FolderOpen className="hidden size-3.5 shrink-0 text-primary group-data-[state=open]/proj:block" />
                      <span className="min-w-0 flex-1 truncate text-[13px] font-medium">
                        {project.name}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onNewChat(project.id)}
                      title="New chat in this project"
                      aria-label={`New chat in “${project.name}”`}
                      className="hidden size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground group-hover/projrow:flex"
                    >
                      <SquarePen className="size-3.5" />
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          aria-label={`Project options for “${project.name}”`}
                          className="hidden size-6 shrink-0 p-0 group-hover/projrow:flex data-[state=open]:flex"
                        >
                          <MoreHorizontal className="size-3.5 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-44">
                        <DropdownMenuItem onClick={() => setSettingsTarget(project)}>
                          <Settings className="mr-2 size-3.5" />
                          Project settings
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setRenameTarget(project)}>
                          <Pencil className="mr-2 size-3.5" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setDeletingProjects([project])}
                        >
                          <Trash2 className="mr-2 size-3.5" />
                          Delete project
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <CollapsibleContent>
                    <div
                      {...chatDropHandlers({
                        key: project.id,
                        setDragOver,
                        onDropChat: (sessionId) => moveChat(sessionId, project.id),
                      })}
                      className={cn(
                        "ml-3 space-y-0.5 rounded-lg border-l border-border/60 pl-2 pt-0.5",
                        dragOver === project.id &&
                          "bg-violet-50/60 dark:bg-violet-950/20"
                      )}
                    >
                      {mounted && loading?.projects && chats.length === 0 ? (
                        <CommandSidebarListSkeleton count={3} />
                      ) : null}
                      {mounted && chats.map((session) => (
                        <ChatRow
                          key={session.id}
                          session={session}
                          active={session.id === state.activeSessionId}
                          projects={state.projects}
                          onSelect={() => openChatFromList(session.id)}
                          onMove={(projectId) => moveChat(session.id, projectId)}
                          onTogglePin={() => actions.togglePinChat(session.id)}
                          onRename={() => setRenameChatTarget(session)}
                          onDragEnd={() => setDragOver(null)}
                          onDelete={() => {
                            onChatDeleted?.(session.id);
                            actions.deleteChat(session.id);
                            toast.success("Chat deleted", {
                              description: `“${session.title}” was removed.`,
                            });
                          }}
                        />
                      ))}
                      {mounted && !loading?.projects && chats.length === 0 ? (
                        <p className="px-2 py-1.5 text-xs text-muted-foreground/60">
                          No chats yet
                        </p>
                      ) : null}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
            {/* grows in place — "View all" lives next to the section title */}
            {lists.projects.remaining > 0 ? (
              <button
                type="button"
                onClick={lists.projects.loadMore}
                className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
              >
                <span className="flex size-5 shrink-0 items-center justify-center">
                  <ChevronDown className="size-3.5" />
                </span>
                Load more
                <span className="ml-auto text-[11px] font-normal text-muted-foreground">
                  {lists.projects.remaining}
                </span>
              </button>
            ) : null}
            {lists.projects.expanded ? (
              <button
                type="button"
                onClick={lists.projects.showLess}
                className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
              >
                <span className="flex size-5 shrink-0 items-center justify-center">
                  <ChevronDown className="size-3.5 rotate-180" />
                </span>
                Show less
              </button>
            ) : null}
            </div>

            <div
              {...chatDropHandlers({
                key: OTHER_CHATS_DROP_KEY,
                setDragOver,
                onDropChat: (sessionId) => moveChat(sessionId, null),
              })}
              className={cn(
                "rounded-lg",
                dragOver === OTHER_CHATS_DROP_KEY &&
                  "bg-violet-50/60 ring-1 ring-inset ring-primary/40 dark:bg-violet-950/20"
              )}
            >
              {mounted && (!query || otherChats.length > 0 || loading?.sessions) && (
                <p className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Other chats
                </p>
              )}
              <div className="space-y-0.5">
                {mounted && loading?.sessions && otherChats.length === 0 ? (
                  <CommandSidebarListSkeleton count={6} />
                ) : null}
                {mounted && otherChats.map((session) => (
                  <ChatRow
                    key={session.id}
                    session={session}
                    active={session.id === state.activeSessionId}
                    projects={state.projects}
                    onSelect={() => openChatFromList(session.id)}
                    onMove={(projectId) => moveChat(session.id, projectId)}
                    onTogglePin={() => actions.togglePinChat(session.id)}
                    onRename={() => setRenameChatTarget(session)}
                    onDragEnd={() => setDragOver(null)}
                    onDelete={() => {
                      onChatDeleted?.(session.id);
                      actions.deleteChat(session.id);
                      toast.success("Chat deleted", {
                        description: `“${session.title}” was removed.`,
                      });
                    }}
                  />
                ))}
                {hasMoreSessions ? (
                  <div ref={sessionsLoadMoreRef} className="py-2">
                    {mounted && isFetchingMoreSessions ? <CommandSidebarListSkeleton count={2} /> : null}
                  </div>
                ) : null}
                {mounted && !query && !loading?.sessions && otherChats.length === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-muted-foreground/60">
                    No ungrouped chats
                  </p>
                ) : null}
              </div>
            </div>

            {nothingMatches ? (
              <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                No chats match “{search.trim()}”.
              </p>
            ) : null}
          </CommandSidebarScrollArea>

          <RenameProjectDialog
            project={renameTarget}
            onClose={() => setRenameTarget(null)}
            onRename={(projectId, name) => {
              actions.renameProject(projectId, name);
              toast.success("Project renamed", {
                description: `Now “${name}”.`,
              });
            }}
          />

          <ProjectSettingsDialog
            project={settingsTarget}
            onClose={() => setSettingsTarget(null)}
            onSave={(projectId, patch) => {
              actions.updateProject(projectId, patch);
              toast.success("Project settings saved", {
                description: `“${patch.name}” was updated.`,
              });
            }}
          />

          <RenameChatDialog
            session={renameChatTarget}
            onClose={() => setRenameChatTarget(null)}
            onRename={(sessionId, title) => {
              actions.renameChat(sessionId, title);
              toast.success("Chat renamed", {
                description: `Now “${title}”.`,
              });
            }}
          />

          <DeleteProjectsDialog
            projects={deletingProjects}
            sessions={state.sessions}
            onClose={() => setDeletingProjects(null)}
            onConfirm={confirmDeleteProjects}
          />

        </div>
      ) : null}

      {/* rendered outside the collapsed/expanded branch so the rail's search
          icon opens it too */}
      <SearchEverythingDialog
        open={searchDialogOpen}
        onOpenChange={setSearchDialogOpen}
        onOpenChat={onOpenChat}
        onOpenProject={onOpenProject}
      />
    </aside>
  );
}

/** Panel for Command v2 pages other than the chat itself — interactions hit
 *  the shared root-layout store live, then land on the chat page. */
export function CommandV2PanelWithNav({
  onNavigate,
  embeddedInSheet = false,
}: {
  /** Called after any row navigation — the mobile sheet closes itself here. */
  onNavigate?: () => void;
  /** Stretch the panel inside the mobile command sidebar sheet. */
  embeddedInSheet?: boolean;
} = {}) {
  const { actions } = useCommandPrototype();

  return (
    <CommandV2Panel
      forceExpanded={embeddedInSheet}
      className={embeddedInSheet ? "border-r-0" : undefined}
      onOpenChat={(sessionId) => {
        actions.selectChat(sessionId);
        onNavigate?.();
      }}
      onOpenProject={(projectId) => {
        actions.selectProject(projectId);
        onNavigate?.();
      }}
      onNewChat={(projectId) => {
        actions.newChat(projectId ?? undefined);
        onNavigate?.();
      }}
    />
  );
}
